/**
 * portal 面の HTTP クライアント（ARCHITECTURE.md §4.3 / §7.1）。
 *
 * 前提:
 * - 業務 API は `/api/v1/portal/**` に固定。認証セレモニーだけ `/api/v1/auth/portal/**`。
 * - access JWT（`aud=portal`, 15 分）は **メモリにだけ**置く。
 *   refresh は httpOnly + path=`/api/v1/auth/portal` の Cookie なので、
 *   リロード後は silent refresh で復帰できる。localStorage に access token を置くと
 *   XSS でそのまま持ち出されるため、置かない（05-ui.md の記述からの意図的な逸脱）。
 * - 401 を受けたら **1 回だけ** silent refresh → 元のリクエストを再試行。
 *   多タブで refresh が同時に走ると Cookie ローテーションが競合するので、
 *   `navigator.locks` で直列化する。
 */
import { parseErrorEnvelope, type ErrorCode } from '@mail-sv/shared';
import { env } from '../env.js';

/** portal 業務 API の prefix。**ここ以外に書かない**。 */
export const PORTAL_PREFIX = '/api/v1/portal';
/** 認証セレモニーの prefix。refresh Cookie の path と一致していなければならない。 */
export const AUTH_PREFIX = '/api/v1/auth/portal';

const REFRESH_LOCK = 'mailsv-portal-auth-refresh';

export class ApiError extends Error {
  readonly status: number;
  /** エラー封筒の `code`。未知の値もあり得るので string 型で受ける。 */
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  is(code: ErrorCode): boolean {
    return this.code === code;
  }
}

/* ------------------------------------------------------------ アクセストークン */

let accessToken: string | null = null;
/** 401 → refresh も失敗、という「本当にログアウトすべき」局面の通知先。 */
let onSessionLost: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function setSessionLostHandler(handler: (() => void) | null): void {
  onSessionLost = handler;
}

/* ------------------------------------------------------------------ 低レベル */

function url(path: string): string {
  return `${env.apiUrl}${path}`;
}

async function toApiError(res: Response): Promise<ApiError> {
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    /* JSON でない応答（502 の HTML など）もあるので握る */
  }
  const envelope = parseErrorEnvelope(body);
  if (envelope) {
    return new ApiError(res.status, envelope.code, envelope.message, envelope.details);
  }
  return new ApiError(
    res.status,
    'INTERNAL_ERROR',
    res.status >= 500
      ? 'サーバーで問題が発生しました。しばらく待って再度お試しください。'
      : '通信に失敗しました。',
  );
}

export interface RequestOptions {
  body?: unknown;
  signal?: AbortSignal;
  /** 作成系 POST の再送安全化。呼び出し側は再試行でも同じ値を送ること。 */
  idempotencyKey?: string;
  /** 認証ヘッダを付けない（ログイン・公開設定など）。 */
  anonymous?: boolean;
  /** 401 時の silent refresh を行わない（refresh 自体・ログイン系で使う）。 */
  noRetry?: boolean;
  /**
   * 画面が閉じられても送りきる（`fetch` の `keepalive`）。
   *
   * **`pagehide` で投げるリクエストには必須。** 付けないとブラウザが
   * 遷移時に接続ごと破棄するので、送ったつもりの解放要求が届かない
   * （ソフトロックが TTL の 5 分間残り、他の担当者が編集に入れなくなる）。
   * 本文つきの要求には使わないこと（keepalive は 64 KiB の上限がある）。
   */
  keepalive?: boolean;
}

async function rawRequest(method: string, path: string, opts: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (!opts.anonymous && accessToken) headers.Authorization = `Bearer ${accessToken}`;
  if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;

  return fetch(url(path), {
    method,
    headers,
    // refresh Cookie を載せるため常に include（CORS 時も同様）。
    credentials: 'include',
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    signal: opts.signal ?? null,
    keepalive: opts.keepalive ?? false,
  });
}

/* -------------------------------------------------------------------- refresh */

let inflightRefresh: Promise<boolean> | null = null;

async function performRefresh(): Promise<boolean> {
  try {
    const res = await rawRequest('POST', `${AUTH_PREFIX}/refresh`, { anonymous: true });
    if (!res.ok) return false;
    const data = (await res.json()) as { accessToken?: unknown };
    if (typeof data.accessToken !== 'string') return false;
    accessToken = data.accessToken;
    return true;
  } catch {
    return false;
  }
}

/**
 * access token を更新する。多タブ・多リクエストで同時に呼ばれても
 * 実際の refresh は 1 回しか走らない。
 */
export async function refreshSession(): Promise<boolean> {
  if (inflightRefresh) return inflightRefresh;

  const run = async (): Promise<boolean> => {
    // navigator.locks は secure context 限定。無い環境（http の LAN 検証など）でも
    // 動くよう、その場合はプロセス内の inflight 排他だけで妥協する。
    if (typeof navigator !== 'undefined' && navigator.locks) {
      return navigator.locks.request(REFRESH_LOCK, () => performRefresh());
    }
    return performRefresh();
  };

  inflightRefresh = run().finally(() => {
    inflightRefresh = null;
  });
  return inflightRefresh;
}

/* ------------------------------------------------------------------ 高レベル */

async function request<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
  let res = await rawRequest(method, path, opts);

  if (res.status === 401 && !opts.noRetry && !opts.anonymous) {
    const ok = await refreshSession();
    if (ok) {
      res = await rawRequest(method, path, opts);
    } else {
      accessToken = null;
      onSessionLost?.();
    }
  }

  if (!res.ok) throw await toApiError(res);

  // 204 / 205 と Content-Length: 0 は本文が無い。JSON パースすると必ず落ちる。
  if (res.status === 204 || res.status === 205) return undefined as T;
  const text = await res.text();
  if (text.length === 0) return undefined as T;
  return JSON.parse(text) as T;
}

/** portal 業務 API（`/api/v1/portal` 配下）。パスは先頭スラッシュ付きの相対で渡す。 */
export const api = {
  get: <T>(path: string, opts?: RequestOptions): Promise<T> =>
    request<T>('GET', `${PORTAL_PREFIX}${path}`, opts),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions): Promise<T> =>
    request<T>('POST', `${PORTAL_PREFIX}${path}`, { ...opts, body }),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions): Promise<T> =>
    request<T>('PATCH', `${PORTAL_PREFIX}${path}`, { ...opts, body }),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions): Promise<T> =>
    request<T>('PUT', `${PORTAL_PREFIX}${path}`, { ...opts, body }),
  del: <T>(path: string, opts?: RequestOptions): Promise<T> =>
    request<T>('DELETE', `${PORTAL_PREFIX}${path}`, opts),
};

/** 認証セレモニー（`/api/v1/auth/portal` 配下）。 */
export const authApi = {
  post: <T>(path: string, body?: unknown, opts?: RequestOptions): Promise<T> =>
    request<T>('POST', `${AUTH_PREFIX}${path}`, { ...opts, body }),
  get: <T>(path: string, opts?: RequestOptions): Promise<T> =>
    request<T>('GET', `${AUTH_PREFIX}${path}`, opts),
  /** 自分のプロフィール更新（`PATCH /me`）だけが使う。 */
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions): Promise<T> =>
    request<T>('PATCH', `${AUTH_PREFIX}${path}`, { ...opts, body }),
  del: <T>(path: string, opts?: RequestOptions): Promise<T> =>
    request<T>('DELETE', `${AUTH_PREFIX}${path}`, opts),
};

/**
 * 無認証の公開 API（`/api/v1/public`）。
 *
 * `anonymous: true` を必ず付ける。付けないと 401 で silent refresh が走り、
 * **ログインしていない訪問者のサインアップ画面でセッション切れの扱いになる**。
 */
export const publicApi = {
  get: <T>(path: string): Promise<T> =>
    request<T>('GET', `/api/v1/public${path}`, { anonymous: true, noRetry: true }),
  post: <T>(path: string, body?: unknown): Promise<T> =>
    request<T>('POST', `/api/v1/public${path}`, { anonymous: true, noRetry: true, body }),
};

/**
 * JSON ではなく本文そのものを返すエンドポイント用（コンテンツのプレビューなど）。
 *
 * `status` を返すのは、プレビューが **202 + JSON**（ビルド中）と
 * **200 + text/html**（成果物）を出し分けるため。ここで JSON.parse すると
 * HTML の側で必ず落ちるので、判定は呼び出し側に任せる。
 */
export async function requestText(
  method: string,
  path: string,
  opts: RequestOptions = {},
): Promise<{ status: number; contentType: string; body: string }> {
  let res = await rawRequest(method, `${PORTAL_PREFIX}${path}`, opts);
  if (res.status === 401 && !opts.noRetry) {
    const ok = await refreshSession();
    if (ok) res = await rawRequest(method, `${PORTAL_PREFIX}${path}`, opts);
    else {
      accessToken = null;
      onSessionLost?.();
    }
  }
  if (!res.ok && res.status !== 202) throw await toApiError(res);
  return {
    status: res.status,
    contentType: res.headers.get('content-type') ?? '',
    body: await res.text(),
  };
}

/** 認証済みの GET でファイルを取得する（請求書 PDF など）。 */
export async function downloadFile(path: string, filename: string): Promise<void> {
  const res = await rawRequest('GET', `${PORTAL_PREFIX}${path}`, {});
  if (!res.ok) throw await toApiError(res);
  const blob = await res.blob();
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // 即時 revoke すると Firefox でダウンロードが中断されることがあるので少し遅らせる。
  setTimeout(() => URL.revokeObjectURL(href), 10_000);
}

/** 画面に出してよいエラーメッセージを取り出す。 */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error && error.message) return error.message;
  return '予期しないエラーが発生しました。';
}

/** 冪等キー。mutation ごとに 1 回だけ生成し、再試行でも同じ値を送る。 */
export function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
